import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Stage } from 'react-konva';
import { Icon } from '@/assets/icons/LibrarySvg';
import BoardElementComponent from '@/components/Board/Elements/BoardElement';
import { useOptimizedRendering, usePreloadPresentationImages, useResetPresentationState, useStageDragEvents, useStageMouseEvents, useStageTouchEvents, } from '@/hooks/board';
import useFullscreen from '@/hooks/useFullScreen';
import { useBoardHistory } from '@/hooks/useHistory';
import { boardStateAtom, draftSelectionIdsAtom, gridSizeAtom, isPanningAtom, isZoomingAtom, presentationBoundsAtom, presentationElementIdAtom, presentationModeAtom, presentationPositionAtom, presentationViewChangedAtom, presentationZoomAtom, roleAtom, selectedIdsAtom, snapToGridAtom, snapToGuidesAtom, stageModeAtom, stageRefAtom, } from '@/store/boardAtoms';
import { createElementDragBoundFunc } from '@/utils/board/insertionUtils';
// Импортируем оптимизированную функцию хеширования
import { computeElementHash } from '@/utils/board/layerRenderingUtils';
import { getElementsMap, getSnappedXY } from '@/utils/board/stageUtils';
import { compareElementsByZIndex, isRectIntersecting } from '@/utils/board/zIndexHelpers';
import { calculatePresentationTransform, getElementBounds, getElementGeometry, PRESENTATION_STYLES, } from '@/utils/presentation';
// TODO: добавить пермишены для токен-пользователей для перемещения токенов в режиме презентации
export const PresentationMode = ({ send }) => {
    /** Роль пользователя */
    const [role] = useAtom(roleAtom);
    /** Размер сетки */
    const [gridSize] = useAtom(gridSizeAtom);
    /** Снаппинг по гайдлиниям */
    const [, setSnapToGuides] = useAtom(snapToGuidesAtom);
    /** Состояние доски (слои и элементы) */
    const { boardState, setBoardState } = useBoardHistory();
    // Стейты режима презентации
    const [presentationMode] = useAtom(presentationModeAtom);
    const [presentationElementId] = useAtom(presentationElementIdAtom);
    const [presentationBounds] = useAtom(presentationBoundsAtom);
    // Стейты изменения позиции и зума презентации пользователем
    // Хуки используют эти атомы напрямую, поэтому setter функции не нужны здесь
    const [presentationZoom] = useAtom(presentationZoomAtom);
    const [presentationPosition] = useAtom(presentationPositionAtom);
    const [presentationViewChanged] = useAtom(presentationViewChangedAtom);
    // Стейты выделения элементов
    const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
    const [draftSelectionIds, setDraftSelectionIds] = useAtom(draftSelectionIdsAtom);
    // Режим работы сцены
    const [stageMode] = useAtom(stageModeAtom);
    const [isPanning] = useAtom(isPanningAtom);
    const [isZooming] = useAtom(isZoomingAtom);
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Референсы для хуков
    const stageRef = useRef(null);
    const [, setStageRef] = useAtom(stageRefAtom);
    const pointerPositionRef = useRef(null);
    const wasStageDraggedRef = useRef(false);
    const elementClickId = useRef(null);
    // Callback ref для синхронизации stageRef с атомом
    const stageRefCallback = useCallback((stage) => {
        stageRef.current = stage;
        setStageRef(stage);
    }, [setStageRef]);
    // Состояние для отслеживания размеров экрана
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    // Отслеживание изменений размеров экрана (включая полноэкранный режим)
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('fullscreenchange', handleResize);
        };
    }, []);
    // Хук для сброса позиции презентации
    const resetPresentationView = useResetPresentationState();
    // Хук для полноэкранного режима
    const { isFullScreen, toggleFullScreen } = useFullscreen({});
    // Хук для предзагрузки изображений всех фреймов (только для зрителей)
    usePreloadPresentationImages();
    // Очищаем атом при размонтировании компонента
    useEffect(() => {
        return () => {
            setStageRef(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Выключаем снаппинг по гайдлиниям для зрителей в режиме презентации
    useEffect(() => {
        if (presentationMode && role === 'VIEWER') {
            setSnapToGuides(false);
            return () => {
                setSnapToGuides(true);
            };
        }
    }, [presentationMode, role, setSnapToGuides]);
    // Очищаем выделение при входе в режим презентации
    useEffect(() => {
        if (presentationMode) {
            setSelectedIds([]);
            setDraftSelectionIds([]);
        }
    }, [presentationMode, setSelectedIds, setDraftSelectionIds]);
    // Обновляем границы презентации при изменении элемента в boardState
    const [, setPresentationBounds] = useAtom(presentationBoundsAtom);
    const presentationElement = useMemo(() => {
        var _a;
        if (!presentationMode || !presentationElementId)
            return null;
        const elementsMap = getElementsMap(boardState.layers);
        return ((_a = elementsMap.get(presentationElementId)) === null || _a === void 0 ? void 0 : _a.el) || null;
    }, [presentationMode, presentationElementId, boardState.layers]);
    const frameGeometry = useMemo(() => (presentationElement ? getElementGeometry(presentationElement) : null), [presentationElement]);
    useEffect(() => {
        if (frameGeometry) {
            setPresentationBounds(frameGeometry.bounds);
        }
    }, [frameGeometry, setPresentationBounds]);
    // Получаем элементы презентации при активации режима
    const presentationElements = useMemo(() => {
        var _a;
        if (!presentationMode || !presentationElementId || !presentationBounds) {
            return [];
        }
        const elementsMap = getElementsMap(boardState.layers);
        const presentationElement = (_a = elementsMap.get(presentationElementId)) === null || _a === void 0 ? void 0 : _a.el;
        if (!presentationElement) {
            return [];
        }
        const elements = [];
        // Добавляем основной элемент презентации
        presentationElement.locked = true;
        elements.push(presentationElement);
        // Добавляем все элементы внутри границ презентации
        for (const layer of boardState.layers || []) {
            if (!layer.visible)
                continue;
            for (const element of layer.elements || []) {
                if (element.id === presentationElementId)
                    continue;
                if (element.isPresentationFrame) {
                    // Запрещаем перемещение и редактирование фрейма для зрителей
                    element.locked = true;
                    element.zIndex = -10;
                }
                const elementBounds = getElementBounds(element);
                if (isRectIntersecting(elementBounds, presentationBounds)) {
                    // Проверяем, находится ли элемент внутри границ презентации
                    elements.push(element);
                }
            }
        }
        // Сортируем элементы по zIndex для правильного порядка рендеринга
        return elements.sort(compareElementsByZIndex);
    }, [presentationMode, presentationElementId, presentationBounds, boardState.layers]);
    // Вычисляем масштаб и смещения для центрирования
    const { scaleX, scaleY, offsetX, offsetY } = useMemo(() => {
        if (!presentationBounds) {
            return { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0 };
        }
        return calculatePresentationTransform(presentationBounds, screenSize);
    }, [presentationBounds, screenSize]);
    // Вычисляем реальный zoom для рендеринга элементов (учитывает масштаб Stage)
    const presentationStageZoom = useMemo(() => {
        return presentationZoom * scaleX;
    }, [presentationZoom, scaleX]);
    // Функция для обрезки элементов, выходящих за границы фрейма презентации
    const clipFunc = useCallback((ctx) => {
        if (!frameGeometry)
            return;
        const [first, ...rest] = frameGeometry.corners;
        ctx.beginPath();
        ctx.moveTo(first.x, first.y);
        for (const point of rest) {
            ctx.lineTo(point.x, point.y);
        }
        ctx.closePath();
    }, [frameGeometry]);
    /** Хук для обработки touch событий трекпада */
    // Хук автоматически использует презентационные атомы при isPresentationMode: true
    const touchEvents = useStageTouchEvents({
        selectedIds,
        isPresentationMode: true,
    });
    // Обработчик колесика мыши для зума (не используется, зум обрабатывается в useStageTouchEvents)
    // Переиспользуем хуки из BoardStage с локальным состоянием
    const mouseEvents = useStageMouseEvents({
        boardState,
        // canEdit: role !== 'VIEWER', // TODO: добавить пермишены для токен-пользователей
        canEdit: true,
        visibleElements: presentationElements,
        pointerPositionRef,
        wasStageDraggedRef,
        elementClickId,
        isPresentationMode: true,
    });
    const dragEvents = useStageDragEvents({
        boardState,
        setBoardState,
        // canEdit: role !== 'VIEWER', // TODO: добавить пермишены для токен-пользователей
        canEdit: true,
        visibleElements: presentationElements,
        send,
        getElementClickInfo: mouseEvents.getElementClickInfo,
        // setPosition не нужен - хук получает презентационные атомы напрямую при isPresentationMode: true
        isPresentationMode: true,
        presentationOffsets: { offsetX, offsetY },
    });
    /** Функция для ограничения перемещения элемента */
    const elementDragBoundFunc = useMemo(() => createElementDragBoundFunc(() => store.get(stageRefAtom), // Используем функцию для получения актуального Stage
    gridSize, () => store.get(snapToGridAtom), // Используем функцию для получения актуального значения атома
    () => store.get(boardStateAtom), // Используем функцию для получения актуального boardState
    getSnappedXY), [gridSize, store]);
    // Быстрое вычисление хеша состояния для отслеживания изменений
    const stateHash = `${presentationElements.length}-${selectedIds.join(',')}-${draftSelectionIds.join(',')}-${stageMode}-${presentationElementId}`;
    /** Функция рендеринга элементов */
    const renderElements = useCallback(() => {
        // Оптимизация: создаем Set один раз перед map() для O(1) поиска вместо O(n) includes
        const selectedIdsSet = selectedIds.length > 0 ? new Set(selectedIds) : new Set();
        const draftSelectionIdsSet = draftSelectionIds.length > 0 ? new Set(draftSelectionIds) : new Set();
        const elementsMap = getElementsMap(boardState.layers);
        return presentationElements
            .map((element) => {
            var _a;
            const layer = (_a = elementsMap.get(element.id)) === null || _a === void 0 ? void 0 : _a.layer;
            if (!layer)
                return null;
            const isMainElement = element.id === presentationElementId;
            const isSelected = !isMainElement && selectedIdsSet.has(element.id);
            const isDraftSelected = !isMainElement && draftSelectionIdsSet.has(element.id);
            const showSelectionBorder = !isMainElement &&
                (isDraftSelected ||
                    (selectedIds.length > 1 && isSelected) ||
                    (isSelected && element.locked));
            const elementHash = computeElementHash(element);
            const zoomForElement = element.elementType === 'text' ? presentationStageZoom : 2;
            return (_jsx(BoardElementComponent, { el: element, selectedIds: isMainElement ? [] : selectedIds, draftSelection: isMainElement ? [] : draftSelectionIds, draggable: true && !element.locked && !(layer.locked || false) && stageMode !== 'move', 
                // TODO: добавить пермишены для токен-пользователей
                layer: layer, elementDragBoundFunc: elementDragBoundFunc, elementHash: elementHash, isSelected: !!isSelected, isDraftSelected: !!isDraftSelected, showSelectionBorder: !!showSelectionBorder, 
                // Используем реальный масштаб для текста для правильного рендеринга, а для остальных элементов - 2 для качества кеширования изображений
                zoom: zoomForElement, disableImageCache: false, isPresentationMode: true }, element.id));
        })
            .filter((el) => el !== null);
    }, [
        presentationElements,
        boardState.layers,
        presentationElementId,
        selectedIds,
        draftSelectionIds,
        stageMode,
        elementDragBoundFunc,
        presentationStageZoom,
    ]);
    /** Рендеринг элементов с оптимизацией для панорамирования/зума */
    const renderedElements = useOptimizedRendering({
        isPanning,
        isZooming,
        stateHash,
        renderFn: renderElements,
        deps: [
            presentationElements,
            selectedIds,
            draftSelectionIds,
            stageMode,
            presentationElementId,
            boardState.layers,
            elementDragBoundFunc,
        ],
    });
    if (!presentationMode || !presentationBounds || role === 'GM')
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(Stage, { ref: stageRefCallback, width: screenSize.width, height: screenSize.height, x: presentationPosition.x + offsetX, y: presentationPosition.y + offsetY, scaleX: presentationZoom * scaleX, scaleY: presentationZoom * scaleY, onMouseDown: mouseEvents.onStageMouseDown, onMouseMove: mouseEvents.onStageMouseMove, onMouseUp: mouseEvents.onStageMouseUp, onMouseLeave: mouseEvents.onStageMouseLeave, onWheel: touchEvents.onWheel, onTouchStart: touchEvents.onTouchStart, onTouchMove: touchEvents.onTouchMove, onTouchEnd: touchEvents.onTouchEnd, onContextMenu: (e) => e.evt.preventDefault(), 
                // draggable={true}
                draggable: stageMode === 'move' || isPanning, onDragStart: dragEvents.onStageDragStart, onDragMove: dragEvents.onStageDragMove, onDragEnd: dragEvents.onStageDragEnd, style: PRESENTATION_STYLES.STAGE, children: _jsx(Layer, { perfectDrawEnabled: false, shadowForStrokeEnabled: false, clipFunc: clipFunc, children: renderedElements }) }), _jsxs("div", { style: PRESENTATION_STYLES.BUTTONS_CONTAINER, children: [presentationViewChanged && (_jsx("button", { onClick: resetPresentationView, style: PRESENTATION_STYLES.RESET_BUTTON, onMouseEnter: (e) => {
                            Object.assign(e.currentTarget.style, PRESENTATION_STYLES.RESET_BUTTON_HOVER);
                        }, onMouseLeave: (e) => {
                            Object.assign(e.currentTarget.style, PRESENTATION_STYLES.RESET_BUTTON);
                        }, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C \u043F\u043E\u0437\u0438\u0446\u0438\u044E" })), _jsx("button", { onClick: toggleFullScreen, style: PRESENTATION_STYLES.FULLSCREEN_BUTTON, children: isFullScreen ? _jsx(Icon, { name: "exit_full_screen_1" }) : _jsx(Icon, { name: "full_screen_1" }) })] })] }));
};
